import React, { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { LockKey, ArrowLeft, Check, Warning } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function ResetPassword() {
  const { lang } = useI18n();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const strength = useMemo(() => scorePassword(pw), [pw]);
  const mismatch = pw2.length > 0 && pw !== pw2;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) {
      setErr(lang === "da" ? "Adgangskoden skal være mindst 8 tegn." : "Password must be at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setErr(lang === "da" ? "Adgangskoderne matcher ikke." : "Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      if (data?.token) localStorage.setItem("tf_token", data.token);
      if (data?.user) setUser(data.user);
      setDone(true);
      setTimeout(() => nav("/dashboard", { replace: true }), 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string") setErr(detail);
      else setErr(lang === "da" ? "Noget gik galt. Prøv igen." : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return <NoToken lang={lang} />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          data-testid="reset-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-zinc-400 hover:text-white mb-8"
        >
          <ArrowLeft weight="bold" size={14} /> {lang === "da" ? "Tilbage til login" : "Back to login"}
        </Link>

        <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center mb-6">
          <span className="font-display text-base text-white">TF</span>
        </div>

        {done ? (
          <div data-testid="reset-done">
            <div className="label-overline text-[#34C759] mb-3">{lang === "da" ? "Klaret!" : "Done!"}</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              {lang === "da" ? "Du er logget ind." : "You're signed in."}
            </h1>
            <p className="text-sm text-zinc-400 mt-4">
              {lang === "da" ? "Sender dig videre…" : "Redirecting…"}
            </p>
          </div>
        ) : (
          <>
            <div className="label-overline text-[#007AFF] mb-3">
              {lang === "da" ? "Nulstil adgangskode" : "Reset password"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              {lang === "da" ? "Vælg en ny kode." : "Set a new password."}
            </h1>
            <p className="text-sm text-zinc-400 mt-4">
              {lang === "da"
                ? "Mindst 8 tegn. Vi anbefaler en blanding af bogstaver, tal og symboler."
                : "At least 8 characters. We recommend mixing letters, numbers and symbols."}
            </p>

            <form onSubmit={onSubmit} className="mt-8 space-y-3">
              <div className="flex items-center gap-3 border border-[#262626] bg-[#141414] px-3 py-3 focus-within:border-[#007AFF]">
                <LockKey size={16} weight="duotone" className="text-zinc-500" />
                <input
                  data-testid="reset-password-new"
                  type={show ? "text" : "password"}
                  required
                  minLength={8}
                  autoFocus
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder={lang === "da" ? "Ny adgangskode" : "New password"}
                  className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500"
                />
                <button
                  type="button"
                  data-testid="reset-password-show"
                  onClick={() => setShow((s) => !s)}
                  className="text-xs text-zinc-500 hover:text-white uppercase tracking-widest"
                >
                  {show ? (lang === "da" ? "Skjul" : "Hide") : (lang === "da" ? "Vis" : "Show")}
                </button>
              </div>

              {pw.length > 0 && (
                <StrengthBar score={strength} lang={lang} />
              )}

              <div className="flex items-center gap-3 border border-[#262626] bg-[#141414] px-3 py-3 focus-within:border-[#007AFF]">
                <LockKey size={16} weight="duotone" className="text-zinc-500" />
                <input
                  data-testid="reset-password-confirm"
                  type={show ? "text" : "password"}
                  required
                  minLength={8}
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  placeholder={lang === "da" ? "Gentag adgangskode" : "Repeat password"}
                  className="bg-transparent flex-1 outline-none text-sm placeholder:text-zinc-500"
                />
              </div>
              {mismatch && (
                <div className="text-xs text-[#FF3B30] flex items-center gap-2">
                  <Warning weight="bold" size={14} />
                  {lang === "da" ? "Adgangskoderne matcher ikke." : "Passwords don't match."}
                </div>
              )}
              {err && (
                <div
                  data-testid="reset-error"
                  className="text-xs text-[#FF3B30] border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2"
                >
                  {err}
                </div>
              )}
              <button
                type="submit"
                data-testid="reset-submit"
                disabled={loading || mismatch || pw.length < 8}
                className="kinetic-btn w-full bg-[#007AFF] hover:bg-[#0a66d0] disabled:opacity-60 text-white px-4 py-3 font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-2"
              >
                {loading ? (
                  lang === "da" ? "Gemmer…" : "Saving…"
                ) : (
                  <>
                    <Check weight="bold" size={16} />
                    {lang === "da" ? "Gem ny adgangskode" : "Save new password"}
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function NoToken({ lang }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <div className="max-w-md text-center" data-testid="reset-no-token">
        <Warning weight="duotone" size={36} color="#FF3B30" className="mx-auto" />
        <h1 className="font-display text-3xl mt-4">
          {lang === "da" ? "Linket er ugyldigt" : "Invalid link"}
        </h1>
        <p className="text-sm text-zinc-400 mt-2">
          {lang === "da"
            ? "Det reset-link du brugte er enten udløbet eller forkert. Bed om et nyt."
            : "The reset link you used is either expired or invalid. Request a new one."}
        </p>
        <Link
          to="/forgot-password"
          data-testid="reset-request-new"
          className="kinetic-btn mt-6 inline-block bg-[#007AFF] hover:bg-[#0a66d0] text-white px-5 py-3 font-bold uppercase tracking-widest text-sm"
        >
          {lang === "da" ? "Bed om nyt link" : "Request a new link"}
        </Link>
      </div>
    </div>
  );
}

function scorePassword(pw) {
  // 0..4 simple heuristic: length + variety
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}

function StrengthBar({ score, lang }) {
  const labels = lang === "da"
    ? ["Svag", "OK", "God", "Stærk", "Meget stærk"]
    : ["Weak", "OK", "Good", "Strong", "Very strong"];
  const colors = ["#FF3B30", "#FF9500", "#FFCC00", "#34C759", "#34C759"];
  return (
    <div className="flex items-center gap-3" data-testid="reset-strength">
      <div className="h-1 flex-1 grid grid-cols-4 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-1"
            style={{ background: i < score ? colors[score] : "#262626" }}
          />
        ))}
      </div>
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: colors[score] }}>
        {labels[score]}
      </span>
    </div>
  );
}
